import { useCallback, useMemo, useState } from 'react'
import { Bot, ShieldAlert, X } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { useQueryClient } from '@tanstack/react-query'
import { IncidentDetailModal } from '../features/incidents/components/IncidentDetailModal'
import { IncidentsHeader } from '../features/incidents/components/IncidentsHeader'
import { KanbanView } from '../features/incidents/components/KanbanView'
import { ListView } from '../features/incidents/components/ListView'
import type { IncidentView } from '../features/incidents/components/ViewToggle'
import { ViewToggle } from '../features/incidents/components/ViewToggle'
import { useIncidents, INCIDENTS_QUERY_KEY } from '../features/incidents/hooks/useIncidents'
import { useUpdateIncidentStatus } from '../features/incidents/hooks/useUpdateIncidentStatus'
import { useTaskNotificationStream } from '../features/incidents/hooks/useTaskNotificationStream'
import type { TaskNotification } from '../features/incidents/hooks/useTaskNotificationStream'
import { useIncidentsFilterStore } from '../features/incidents/store/incidentsFilterStore'
import { EmptyState } from '../components/EmptyState'
import { LoadErrorState } from '../components/LoadErrorState'
import { useSelectedPropertyName } from '../store/propertyStore'
import { getPropertyAwareCopy } from '../utils/propertyCopy'
import type { Incident } from '../features/incidents/types/incident.types'


export function IncidentsPage(): JSX.Element {
  const { data, isLoading, isError, refetch, isFetching } = useIncidents()
  
  const apiIncidents = data ?? []
  const incidents = apiIncidents
  

  const priorities = useIncidentsFilterStore((state) => state.priorities)
  const statuses = useIncidentsFilterStore((state) => state.statuses)
  const clearFilters = useIncidentsFilterStore((state) => state.clearFilters)
  const propertyName = useSelectedPropertyName()
  const queryClient = useQueryClient()
  const updateStatus = useUpdateIncidentStatus()

  const [view, setView] = useState<IncidentView>('kanban')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [dismissedDraftIds, setDismissedDraftIds] = useState<Set<number>>(() => new Set())

  // Real-time task push via SSE — skip for dummy/preview data
  useTaskNotificationStream({
    onNew: useCallback((n: TaskNotification) => {
      toast.success(`New incident: ${n.title}`, { duration: 6000 })
      void queryClient.invalidateQueries({ queryKey: [INCIDENTS_QUERY_KEY] })
    }, [queryClient]),
    onDraft: useCallback(() => {
      void queryClient.invalidateQueries({ queryKey: [INCIDENTS_QUERY_KEY] })
    }, [queryClient]),
  })

  const filteredIncidents = useMemo(() => {
    return incidents.filter((incident) => {
      const priorityOk =
        priorities.length === 0 || priorities.includes(incident.priority)
      const statusOk = statuses.length === 0 || statuses.includes(incident.status)
      return priorityOk && statusOk
    })
  }, [incidents, priorities, statuses])

  // AI DRAFT incidents that haven't been locally dismissed
  const draftAlerts = useMemo(
    () =>
      incidents.filter(
        (i) => i.status === 'DRAFT' && i.source === 'ai' && !dismissedDraftIds.has(i.id),
      ),
    [incidents, dismissedDraftIds],
  )

  const hasActiveFilters = priorities.length > 0 || statuses.length > 0
  const emptyCopy = getPropertyAwareCopy(propertyName)

  return (
    <div className="flex h-full flex-col gap-6 overflow-hidden">
      <IncidentsHeader />

      {/* DRAFT AI suggestion banners */}
      {draftAlerts.length > 0 && (
        <DraftAlertStrip
          drafts={draftAlerts}
          onPromote={(id) => updateStatus.mutate({ id, body: { status: 'NEW' } })}
          onDismiss={(id) => {
            setDismissedDraftIds((prev) => new Set([...prev, id]))
            updateStatus.mutate({ id, body: { status: 'CLOSED' } })
          }}
          onSelect={setSelectedId}
          isUpdating={updateStatus.isPending}
        />
      )}

      <ViewToggle value={view} onChange={setView} />

      <div className="flex flex-1 flex-col overflow-y-auto">
        {isLoading && <LoadingState view={view} />}
        {isError && (
          <LoadErrorState
            message="Couldn't load incidents"
            onRetry={() => refetch()}
            retrying={isFetching}
          />
        )}
        {!isLoading && !isError && incidents.length === 0 && (
          <EmptyState
            icon={ShieldAlert}
            title={emptyCopy.title}
            message={emptyCopy.message}
          />
        )}
        {!isLoading && !isError && incidents.length > 0 && filteredIncidents.length === 0 && (
          <EmptyState
            title="No incidents match the current filter"
            message="Try clearing the active filters to see more incidents."
            actionLabel={hasActiveFilters ? 'Clear filters' : undefined}
            onAction={hasActiveFilters ? clearFilters : undefined}
          />
        )}
        {!isLoading && !isError && filteredIncidents.length > 0 && (
          view === 'kanban' ? (
            <KanbanView incidents={filteredIncidents} onSelect={setSelectedId} />
          ) : (
            <ListView incidents={filteredIncidents} onSelect={setSelectedId} />
          )
        )}
      </div>

      {selectedId !== null && (
        <IncidentDetailModal
          incidentId={selectedId}
          onClose={() => setSelectedId(null)}
          prefetched={undefined}
        />
      )}
    </div>
  )
}

interface DraftAlertStripProps {
  drafts: Incident[]
  onPromote: (id: number) => void
  onDismiss: (id: number) => void
  onSelect: (id: number) => void
  isUpdating: boolean
}

function DraftAlertStrip({ drafts, onPromote, onDismiss, onSelect, isUpdating }: DraftAlertStripProps): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      {drafts.map((draft) => {
        const pct = draft.confidence != null ? Math.round(draft.confidence * 100) : null
        return (
          <div
            key={draft.id}
            className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5"
          >
            <Bot className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <button
                type="button"
                onClick={() => onSelect(draft.id)}
                className="truncate text-sm font-medium text-amber-900 hover:underline text-left"
              >
                {draft.title}
              </button>
              {(draft.intent || pct != null) && (
                <p className="mt-0.5 text-xs text-amber-700">
                  AI suggestion
                  {draft.intent ? ` · ${draft.intent}` : ''}
                  {pct != null ? ` · ${pct}% confidence` : ''}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                disabled={isUpdating}
                onClick={() => onPromote(draft.id)}
                className="rounded-md bg-amber-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
              >
                Promote
              </button>
              <button
                type="button"
                disabled={isUpdating}
                onClick={() => onDismiss(draft.id)}
                className="rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-50 disabled:opacity-50"
              >
                Dismiss
              </button>
              <button
                type="button"
                onClick={() => onDismiss(draft.id)}
                className="text-amber-500 hover:text-amber-700"
                aria-label="Dismiss suggestion"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function LoadingState({ view }: { view: IncidentView }): JSX.Element {
  if (view === 'kanban') {
    return (
      <div className="flex gap-4 overflow-x-auto pb-2">
        {Array.from({ length: 7 }).map((_, columnIndex) => (
          <div key={columnIndex} className="flex w-60 shrink-0 flex-col gap-3">
            <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
            {Array.from({ length: 3 }).map((__, cardIndex) => (
              <div
                key={cardIndex}
                className="h-20 animate-pulse rounded-lg bg-gray-100"
              />
            ))}
          </div>
        ))}
      </div>
    )
  }
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="h-12 animate-pulse rounded bg-gray-100" />
      ))}
    </div>
  )
}
